import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, GripVertical, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { DEFAULT_ICON_NAME, ICON_OPTIONS, iconByName, iconForCategory } from '@/lib/categoryIcons';
import FlagBadge from '@/components/FlagBadge';
import {
  FLAG_OPTIONS,
  flagByKey,
  flagGradient,
  toFlagKey,
  type CountryFlag,
  type FlagKey,
} from '@shared/flags';
import type { Category, CategoryInput, CategoryPatch } from '@shared/schemas';

const EMPTY_NEW: CategoryInput = {
  name: '',
  emoji: DEFAULT_ICON_NAME,
  flag: null,
  sort_order: 0,
};

export default function AdminCategories() {
  const queryClient = useQueryClient();
  const categoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: api.listCategories,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['categories'] });
    queryClient.invalidateQueries({ queryKey: ['recipes'] });
  };

  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [draft, setDraft] = useState<CategoryInput>(EMPTY_NEW);
  const createMutation = useMutation({
    mutationFn: (input: CategoryInput) =>
      api.adminCreateCategory({
        ...input,
        sort_order: categoriesQuery.data?.length ?? 0,
      }),
    onSuccess: () => {
      setDraft(EMPTY_NEW);
      setError(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Vytvoření selhalo'),
  });

  const patchMutation = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: CategoryPatch }) =>
      api.adminPatchCategory(id, patch),
    onSuccess: () => {
      setEditingId(null);
      setError(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Uložení selhalo'),
  });

  const deleteMutation = useMutation({
    mutationFn: api.adminDeleteCategory,
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Smazání selhalo'),
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
  );

  const categories = categoriesQuery.data ?? [];

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = categories.findIndex((c) => c.id === active.id);
    const newIndex = categories.findIndex((c) => c.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(categories, oldIndex, newIndex).map((c, i) => ({
      ...c,
      sort_order: i,
    }));
    queryClient.setQueryData(['categories'], reordered);

    const toPatch = reordered.filter(
      (c, i) => categories.find((o) => o.id === c.id)?.sort_order !== i,
    );
    Promise.all(toPatch.map((c) => api.adminPatchCategory(c.id, { sort_order: c.sort_order })))
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['recipes'] });
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Uložení pořadí selhalo');
        invalidate();
      });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 rounded-full p-2 text-text-muted hover:bg-accent-soft hover:text-accent"
            aria-label="Zpět na recepty"
          >
            <ArrowLeft size={18} />
          </Link>
          <h1 className="font-serif text-3xl tracking-tight">Kategorie</h1>
        </div>
      </div>

      {error && (
        <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </p>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-medium uppercase tracking-wider text-text-muted">
          Nová kategorie
        </h2>
        <form
          className="grid gap-2 rounded-2xl border border-border bg-surface p-3 sm:grid-cols-[5rem_1fr_10rem_auto]"
          onSubmit={(e) => {
            e.preventDefault();
            if (!draft.name.trim()) return;
            createMutation.mutate(draft);
          }}
        >
          <Field label="Ikona">
            <IconPicker
              value={draft.emoji}
              onChange={(name) => setDraft((d) => ({ ...d, emoji: name }))}
            />
          </Field>
          <Field label="Název">
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
              required
              maxLength={40}
              className={inputClass}
            />
          </Field>
          <Field label="Vlajka">
            <FlagPicker value={draft.flag} onChange={(flag) => setDraft((d) => ({ ...d, flag }))} />
          </Field>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={createMutation.isPending || !draft.name.trim()}
              className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-xl bg-accent px-4 text-sm font-medium text-white shadow-card transition-colors hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
            >
              <Plus size={16} /> Přidat
            </button>
          </div>
        </form>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-medium uppercase tracking-wider text-text-muted">
          Stávající kategorie
        </h2>

        {categoriesQuery.isPending && <p className="text-sm text-text-muted">Načítám…</p>}

        {!categoriesQuery.isPending && categories.length === 0 && (
          <p className="rounded-2xl border border-dashed border-border bg-surface p-6 text-center text-text-muted">
            Zatím žádné kategorie.
          </p>
        )}

        {categories.length > 0 && (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={categories.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-surface">
                {categories.map((c) =>
                  editingId === c.id ? (
                    <CategoryEditRow
                      key={c.id}
                      category={c}
                      onCancel={() => {
                        setEditingId(null);
                        setError(null);
                      }}
                      onSave={(patch) => patchMutation.mutate({ id: c.id, patch })}
                      saving={patchMutation.isPending}
                    />
                  ) : (
                    <SortableRow
                      key={c.id}
                      category={c}
                      onEdit={() => {
                        setEditingId(c.id);
                        setError(null);
                      }}
                      onDelete={() => {
                        if (confirm(`Smazat kategorii „${c.name}"?`)) deleteMutation.mutate(c.id);
                      }}
                    />
                  ),
                )}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </section>
    </div>
  );
}

const inputClass =
  'h-10 w-full rounded-xl border border-border bg-bg px-3 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-text-muted">{label}</span>
      {children}
    </label>
  );
}

// Both pickers render their dropdown into a portal with fixed positioning so it
// can never be clipped by the list's `overflow-hidden`, and flip it above the
// trigger when there isn't room below (e.g. the last category, near the bottom
// of the page). `width` is the dropdown's own width, used to keep it on screen.
function useDropdownAnchor(open: boolean, width: number) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number; maxH: number }>();

  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const btn = btnRef.current;
      if (!btn) return;
      const r = btn.getBoundingClientRect();
      const gap = 4;
      const margin = 8;
      const left = Math.max(margin, Math.min(r.left, window.innerWidth - width - margin));
      const spaceBelow = window.innerHeight - r.bottom - margin;
      const spaceAbove = r.top - margin;
      if (spaceBelow < 220 && spaceAbove > spaceBelow) {
        setPos({ left, bottom: window.innerHeight - r.top + gap, maxH: Math.max(140, spaceAbove) });
      } else {
        setPos({ left, top: r.bottom + gap, maxH: Math.max(140, spaceBelow) });
      }
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, width]);

  return { btnRef, pos };
}

function IconPicker({ value, onChange }: { value: string; onChange: (name: string) => void }) {
  const [open, setOpen] = useState(false);
  const { btnRef, pos } = useDropdownAnchor(open, 240); // matches w-60
  const Active = iconByName(value);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Vybrat ikonu"
        className="flex h-10 w-full items-center justify-center rounded-xl border border-border bg-bg text-text hover:border-accent"
      >
        <Active size={20} />
      </button>
      {open &&
        pos &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-40 cursor-pointer"
              onClick={() => setOpen(false)}
              aria-hidden
            />
            <div
              className="fixed z-50 grid w-60 grid-cols-6 gap-1 overflow-y-auto rounded-xl border border-border bg-surface p-2 shadow-card-lg"
              style={{ left: pos.left, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxH }}
            >
              {ICON_OPTIONS.map(({ name, Icon }) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => {
                    onChange(name);
                    setOpen(false);
                  }}
                  aria-label={name}
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-lg hover:bg-accent-soft',
                    value === name && 'bg-accent text-white',
                  )}
                >
                  <Icon size={18} />
                </button>
              ))}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

function FlagSwatch({ flag, className }: { flag: CountryFlag; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn('h-5 w-5 shrink-0 rounded-full ring-1 ring-black/10', className)}
      style={{ backgroundImage: flagGradient(flag) }}
    />
  );
}

// Optional country for categories of origin (Itálie, Španělsko, Thajsko…). Only
// the well-known world cuisines are offered; regions like Asie keep "Bez
// vlajky", which is also the default for every other category.
function FlagPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (key: FlagKey | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const { btnRef, pos } = useDropdownAnchor(open, 224); // matches w-56
  const active = flagByKey(value);

  const pick = (key: FlagKey | null) => {
    onChange(key);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-10 w-full items-center gap-2 rounded-xl border border-border bg-bg px-3 text-sm text-text hover:border-accent"
      >
        {active ? (
          <>
            <FlagSwatch flag={active} />
            <span className="truncate">{active.name}</span>
          </>
        ) : (
          <>
            <span
              aria-hidden
              className="h-5 w-5 shrink-0 rounded-full border border-dashed border-border"
            />
            <span className="truncate text-text-muted">Bez vlajky</span>
          </>
        )}
      </button>
      {open &&
        pos &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-40 cursor-pointer"
              onClick={() => setOpen(false)}
              aria-hidden
            />
            <div
              className="fixed z-50 flex w-56 flex-col gap-0.5 overflow-y-auto rounded-xl border border-border bg-surface p-2 shadow-card-lg"
              style={{ left: pos.left, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxH }}
            >
              <button
                type="button"
                onClick={() => pick(null)}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-accent-soft',
                  value === null && 'bg-accent text-white',
                )}
              >
                <span
                  aria-hidden
                  className="h-5 w-5 shrink-0 rounded-full border border-dashed border-current opacity-60"
                />
                Bez vlajky
              </button>
              {FLAG_OPTIONS.map(({ key, flag }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => pick(key)}
                  className={cn(
                    'flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-accent-soft',
                    value === key && 'bg-accent text-white',
                  )}
                >
                  <FlagSwatch flag={flag} />
                  {flag.name}
                </button>
              ))}
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}

function SortableRow({
  category,
  onEdit,
  onDelete,
}: {
  category: Category;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const Icon = iconForCategory(category);

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center gap-2 px-2 py-3 sm:gap-3 sm:px-4',
        isDragging && 'z-10 bg-surface-elevated shadow-card',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab touch-none rounded p-1 text-text-muted hover:bg-accent-soft active:cursor-grabbing"
        aria-label="Přesunout"
      >
        <GripVertical size={16} />
      </button>
      <span className="relative h-9 w-9 shrink-0" aria-hidden>
        <span className="flex h-full w-full items-center justify-center rounded-lg bg-accent-soft text-text">
          <Icon size={18} />
        </span>
        <FlagBadge flag={category.flag} size={14} ring={1.5} className="border-surface" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{category.name}</p>
        <p className="truncate text-xs text-text-muted">/{category.slug}</p>
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm text-text-muted hover:bg-accent-soft hover:text-accent"
      >
        <Pencil size={14} />
        <span className="hidden sm:inline">Upravit</span>
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm text-text-muted hover:bg-red-50 hover:text-red-700"
      >
        <Trash2 size={14} />
      </button>
    </li>
  );
}

function CategoryEditRow({
  category,
  onCancel,
  onSave,
  saving,
}: {
  category: Category;
  onCancel: () => void;
  onSave: (patch: CategoryPatch) => void;
  saving: boolean;
}) {
  const [name, setName] = useState(category.name);
  const [slug, setSlug] = useState(category.slug);
  const [emoji, setEmoji] = useState(category.emoji);
  const [flag, setFlag] = useState<FlagKey | null>(toFlagKey(category.flag));

  const submit = () => {
    const patch: CategoryPatch = {};
    if (name.trim() !== category.name) patch.name = name.trim();
    if (slug.trim() !== category.slug) patch.slug = slug.trim();
    if (emoji !== category.emoji) patch.emoji = emoji;
    if (flag !== category.flag) patch.flag = flag;
    if (Object.keys(patch).length === 0) {
      onCancel();
      return;
    }
    onSave(patch);
  };

  return (
    <li className="px-4 py-3">
      <form
        className="grid gap-2 sm:grid-cols-[4.5rem_1fr_1fr_9rem_auto]"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label="Ikona">
          <IconPicker value={emoji} onChange={setEmoji} />
        </Field>
        <Field label="Název">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={40}
            className={inputClass}
          />
        </Field>
        <Field label="Slug">
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            required
            maxLength={40}
            pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
            className={inputClass}
          />
        </Field>
        <Field label="Vlajka">
          <FlagPicker value={flag} onChange={setFlag} />
        </Field>
        <div className="flex items-end gap-1">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex h-10 items-center justify-center gap-1 rounded-xl bg-accent px-3 text-sm font-medium text-white shadow-card transition-colors hover:bg-accent/90 disabled:opacity-50"
          >
            <Check size={16} />
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-10 items-center justify-center gap-1 rounded-xl border border-border bg-bg px-3 text-sm text-text-muted hover:text-text"
          >
            <X size={16} />
          </button>
        </div>
      </form>
    </li>
  );
}
